import {CompanyViewType, PolicyFilter} from 'modules/decisionsNext/api/types';

import {PolicyFilterKey} from 'core/constants/policyFilter';

export enum MarketEntityType {
  Carrier = 'CARRIER',
  Intermediary = 'INTERMEDIARY',
}

export const carrierViewTypesByLevel = [
  CompanyViewType.UltimateParentCarrier,
  CompanyViewType.ParentCarrier,
  CompanyViewType.IssuingPaper,
];

export const intermediaryViewTypesByLevel = [
  CompanyViewType.UltimateParentIntermediary,
  CompanyViewType.ParentIntermediary,
  CompanyViewType.Intermediary,
];

export const companyViewTypeByRoleAndLevel = {
  [MarketEntityType.Carrier]: carrierViewTypesByLevel,
  [MarketEntityType.Intermediary]: intermediaryViewTypesByLevel,
};

export const companyRoleByViewType = {
  [CompanyViewType.UltimateParentCarrier]: MarketEntityType.Carrier,
  [CompanyViewType.ParentCarrier]: MarketEntityType.Carrier,
  [CompanyViewType.IssuingPaper]: MarketEntityType.Carrier,
  [CompanyViewType.UltimateParentIntermediary]: MarketEntityType.Intermediary,
  [CompanyViewType.ParentIntermediary]: MarketEntityType.Intermediary,
  [CompanyViewType.Intermediary]: MarketEntityType.Intermediary,
};

type GenericItem = {id: string; availableViewModes: CompanyViewType[]};

type FulfillMarketTreeParams<T> = {
  items: T[];
  role: MarketEntityType;
  transformId?: (item: T, viewType: CompanyViewType) => string;
  currentLevel?: number;
  withDuplicates?: boolean;
};

/**
 * It makes duplicate of items with multiple view modes
 * i.e. if you have an item:
 * {
 *   id: 1,
 *   availableViewModes: [
 *     CompanyViewType.UltimateParentCarrier,
 *     CompanyViewType.ParentCarrier,
 *     CompanyViewType.IssuingPaper,
 *   ],
 * }
 *
 * it will give you:
 * {
 *   id: 1,
 *   availableViewModes,
 *   children: {
 *     id: 1,
 *     availableViewModes,
 *     children: {
 *       id: 1,
 *       availableViewModes
 *     }
 *   }
 * }
 *
 */
export function fulfillMarketTree<T extends GenericItem | (GenericItem & {children?: T[]})>({
  items,
  role,
  transformId,
  currentLevel = 0,
  withDuplicates = true,
}: FulfillMarketTreeParams<T>): (T & {level: CompanyViewType})[] {
  return items.map(item => {
    const levelShift = currentLevel === 0 ? getLevelShift(item, role) : 0;
    currentLevel = currentLevel + levelShift;
    const nextLevel = currentLevel + 1;

    let result = {...item};
    let children = ('children' in item && item.children) || [];

    const currentLevelType = companyViewTypeByRoleAndLevel[role][currentLevel];
    const nextLevelType = companyViewTypeByRoleAndLevel[role][nextLevel];

    if (withDuplicates && nextLevelType && item.availableViewModes.includes(nextLevelType)) {
      const {currentLevelItems = [], nextLevelItems} = getItemsByLevel(children, role, nextLevel);
      const clonedChild: T = {
        ...item,
        children: nextLevelItems,
      };
      children = [clonedChild, ...currentLevelItems];
      result = {
        ...item,
        children,
      };
    }

    const fulfilledChildren = fulfillMarketTree({
      items: children,
      role,
      transformId,
      currentLevel: nextLevel,
      withDuplicates,
    });

    return {
      ...result,
      id: transformId ? transformId(item, currentLevelType) : item.id,
      level: currentLevelType,
      children: fulfilledChildren,
    };
  });
}

export function transformMarketTreeWithUniqIds<T extends GenericItem | (GenericItem & {children?: T[]})>({
  items,
  role,
  transformId,
  currentLevel = 0,
}: Omit<FulfillMarketTreeParams<T>, 'withDuplicates'>): (T & {level: CompanyViewType})[] {
  return fulfillMarketTree({items, role, transformId, currentLevel, withDuplicates: false});
}

const viewTypeSeparator = '%_%';

export function getMarketInternalId(id: string, viewType: CompanyViewType) {
  return [id, viewType].join(viewTypeSeparator);
}

export function getMarketOriginalId(id: string) {
  const [originalId] = id.split(viewTypeSeparator);
  return originalId;
}

export function getMarketViewType(id: string): CompanyViewType {
  const [_, viewType] = id.split(viewTypeSeparator);
  return viewType as CompanyViewType;
}

function getLevelShift(item: GenericItem, primaryRole: MarketEntityType) {
  const {
    availableViewModes = [
      primaryRole === MarketEntityType.Carrier
        ? CompanyViewType.UltimateParentCarrier
        : CompanyViewType.UltimateParentIntermediary,
    ],
  } = item;
  const availableViewModesSet = new Set(availableViewModes);

  return Math.max(
    companyViewTypeByRoleAndLevel[primaryRole].findIndex(type => availableViewModesSet.has(type)),
    0
  );
}

function getItemsByLevel<T extends GenericItem | (GenericItem & {children?: T[]})>(
  items: T[],
  role: MarketEntityType,
  currentLevel: number
) {
  const currentLevelItems: T[] = [];
  const nextLevelItems: T[] = [];
  const currentLevelRole = companyViewTypeByRoleAndLevel[role][currentLevel];

  items.forEach(item => {
    if (!hasAnyTypeOfRole(item.availableViewModes, role) || item.availableViewModes.includes(currentLevelRole)) {
      currentLevelItems.push(item);
    } else {
      nextLevelItems.push(item);
    }
  });

  return {
    currentLevelItems: currentLevelItems.length ? currentLevelItems : undefined,
    nextLevelItems: nextLevelItems.length ? nextLevelItems : undefined,
  };
}

function hasAnyTypeOfRole(types: CompanyViewType[], role: MarketEntityType) {
  const typesByRole = new Set(companyViewTypeByRoleAndLevel[role]);
  return types.some(item => typesByRole.has(item));
}

export const carrierPostfixByIndex = ['Ultimate Parent', 'Parent', 'Issuing Paper'];
export const intermediariesPostfixByIndex = ['Ultimate Parent', 'Parent', 'Intermediary'];

export const CompanyViewTypeToHumanReadableMap = {
  [CompanyViewType.Intermediary]: 'Intermediary',
  [CompanyViewType.IssuingPaper]: 'Issuing Paper',
  [CompanyViewType.ParentCarrier]: 'Parent',
  [CompanyViewType.ParentIntermediary]: 'Parent',
  [CompanyViewType.UltimateParentCarrier]: 'Ultimate Parent',
  [CompanyViewType.UltimateParentIntermediary]: 'Ultimate Parent',
};

type CarrierOrIntermediary<Role> = Role extends MarketEntityType.Intermediary
  ? CompanyViewType.UltimateParentIntermediary | CompanyViewType.ParentIntermediary | CompanyViewType.Intermediary
  : CompanyViewType.UltimateParentCarrier | CompanyViewType.ParentCarrier | CompanyViewType.IssuingPaper;

export const getHighestViewTypeOfRole = <
  Role extends MarketEntityType,
  CompanyRole extends CarrierOrIntermediary<Role>
>(
  viewTypes: CompanyViewType[],
  role: Role
) => {
  const rolesByType = companyViewTypeByRoleAndLevel[role] as CompanyRole[];
  return rolesByType.find(roleByType => viewTypes.includes(roleByType)) || rolesByType[0];
};

export const viewTypeToPolicyFilterMap: Record<CompanyViewType, PolicyFilterKey> = {
  [CompanyViewType.Intermediary]: 'childIntermediaryIds',
  [CompanyViewType.IssuingPaper]: 'issuingPaperIds',
  [CompanyViewType.ParentCarrier]: 'parentCarrierIds',
  [CompanyViewType.ParentIntermediary]: 'parentIntermediaryIds',
  [CompanyViewType.UltimateParentCarrier]: 'ultimateCarrierIds',
  [CompanyViewType.UltimateParentIntermediary]: 'ultimateIntermediaryIds',
};

export function getHighestViewTypePolicyKey(viewTypes: CompanyViewType[], role: MarketEntityType) {
  const highestViewType = getHighestViewTypeOfRole(viewTypes, role);
  return viewTypeToPolicyFilterMap[highestViewType];
}

export function getHighestViewTypeFilter(
  viewTypes: CompanyViewType[],
  role: MarketEntityType,
  id: string
): PolicyFilter {
  const highestViewType = getHighestViewTypeOfRole(viewTypes, role);
  return {
    [viewTypeToPolicyFilterMap[highestViewType]]: id,
  };
}
